import React, { useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import CustomTooltip from '../../../Tooltip';
import Loading from '../../../Loading';
import { statuses } from '../../../../config';
import { getAvailableDevices } from '../../../modules';
import { renderCplAmount, renderCps, renderCplAmountMin } from '../../../modules/index';
import { I18nContext } from 'utils/I18n/context';
import { OfferContext } from '../OfferPageContainer/OfferPageContainer';

const OfferPageGoals = ({ goalEntries = [], goalActions = [], regions = {}, offer = {}, loading = false }) => {
    const { t } = useContext(I18nContext);
    const offerCtx = useContext(OfferContext) || {};
    const showConstructor = offerCtx.showConstructor || (() => {});

    const showConstructorHandler = useCallback((goalEntryId, access) => {
        const linkId = offer?.default_link?.id;
        showConstructor({ linkId, goalEntryId, access });
    }, [showConstructor, offer]);

    const getAvailableRegions = useCallback((goalEntry) => {
        let names = [];
        const restrictions = goalEntry?.goal?.traffic_restrictions || {};
        if (restrictions.regions) {
            names = restrictions.regions.map((regionId) => regions[regionId]);
        }
        return names.join(', ');
    }, [regions]);

    const getGoalActions = useCallback((goalId) => {
        return goalActions.filter((goalAction) => goalAction.goal.id === goalId);
    }, [goalActions]);

    const renderConversionCap = useCallback(({ conversion_cap }) => {
        if (!conversion_cap) return null;
        const { period, cap_limit } = conversion_cap;
        const periods = t?.periods || {};
        const cap = t?.offersPage?.offer?.cap || 'cap';
        const conversions = t?.offersPage?.offer?.conversions || 'conversions';
        return (
            <span className="goals-cap">{periods[period]} {cap} - {cap_limit} {conversions}</span>
        );
    }, [t]);

    const renderGoalActions = useCallback((goalEntry, index) => {
        const headers = t?.dataTable?.headers || {};
        return (
            <div className="item-actions" key={index}>
                <div className="item-actions__wrapper">
                    <div className="item-actions__wrapper-header">
                        <div className="header-item">{headers.action}</div>
                        <div className="header-item">{headers.type}</div>
                        <div className="header-item">{headers.cpaFixed}</div>
                        <div className="header-item">{headers.cpaShared}</div>
                        <div className="header-item">{headers.cplAmountMin}</div>
                    </div>
                    <div className="item-actions__wrapper-body">
                        {getGoalActions(goalEntry.goal.id).map((goalAction, i) => (
                            <div className="body-wrap" key={i}>
                                <div className="body-item">{goalAction.action.title}</div>
                                <div className="body-item">{goalAction.action.type}</div>
                                <div className="body-item">{goalAction.commission_contract && renderCplAmount(goalAction.commission_contract, `commissionContract-${index}-${i}`)}</div>
                                <div className="body-item">{goalAction.commission_contract && renderCps(goalAction.commission_contract, `interestContract-${index}-${i}`)}</div>
                                <div className="body-item">{goalAction.commission_contract && renderCplAmountMin(goalAction.commission_contract, `interestContract-${index}-${i}`)}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }, [t, getGoalActions]);

    const renderGoalEntries = useCallback((goalEntry, key) => {
        let accessIconClass = goalEntry.access_granted ? 'unlock' : 'lock';
        let regionsStr = getAvailableRegions(goalEntry);
        let devices = getAvailableDevices(goalEntry.goal.traffic_restrictions.devices);
        let staticNames = t?.offersPage?.offer || {};

        return (
            <div className="offer-item__goals-item" key={key}>
                <div className="item-header">
                    <span className="num-lock-status">
                        <span className="goal_id">#{goalEntry.goal.id}</span>
                        <span className={`lock-status-ico fa fa-${accessIconClass}`} />
                        {goalEntry.status === statuses.paused
                            ? (
                                <CustomTooltip id={`icon-paused-${key}`} tooltip={staticNames.paused}>
                                    <span className={`paused-status-ico fa fa-pause-circle`} />
                                </CustomTooltip>
                            )
                            : null
                        }
                        {renderConversionCap(goalEntry)}
                    </span>
                    <span className="goals-header">
                        {goalEntry.status === statuses.paused ? '[ PAUSED ] ' : ''}
                        {goalEntry.goal.title}
                    </span>
                    {offer?.default_link
                        ? (
                            <a
                                className="link-icon"
                                href="#!"
                                onClick={() => { showConstructorHandler(goalEntry.id, goalEntry.access_granted); }}
                            >
                                <span className="fa fa-link" />
                            </a>
                        )
                        : null
                    }
                </div>
                <div className="item-description">
                    <p>{goalEntry.goal.description}</p>
                </div>
                {renderGoalActions(goalEntry, key)}
            </div>
        );
    }, [getAvailableRegions, t, offer, showConstructorHandler, renderGoalActions]);

    const staticNames = t?.offersPage?.offer || {};
    const goalEntry = goalEntries[0] || {};

    return (
        <div className="offer-item__goals-list">
            <div className="offer-item__offer-info">
                <div className="offers__item-descr">
                    <p className="offer-name bold">
                        {goalEntry.status === statuses.paused ? '[ PAUSED ] ' : ''}
                        {goalEntry.offer?.title}
                    </p>
                    <div className="offers__item-description"> <span> {goalEntry.goal?.description} </span></div>
                    <div className="offers__item-goal"> <span>{staticNames.goal}:</span>
                        <span className="goal-name">{goalEntry.goal?.title}</span>
                    </div>
                    <p className="offers__item-locales">
                        {staticNames.geo}: {Array.isArray(goalEntry.goal?.offer?.locales) && goalEntry.goal.offer.locales.map((locale, i) => (
                        <span className={`fi fi-${locale}`} key={i}></span>
                    ))}
                    </p>
                </div>
            </div>
            <div className="offer-item__offer-description"></div>
            {loading
                ? <Loading center={true} />
                : goalEntries.map(renderGoalEntries)
            }
        </div>
    );
};

export default OfferPageGoals;
